import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  AuditCaseDetail,
  AuditEvidence,
  AuditFinding,
  AuditResponse,
  AuditSourceResource,
  AuditSourceTimelineEvent,
  AuditTemplateQuestion
} from '../audit.models';
import { AuditService } from '../audit.service';
import {
  auditNavItems,
  evidenceTypeOptions,
  formatAuditDate,
  responseResultOptions,
  reviewResultOptions,
  severityOptions,
  statusSeverity
} from '../audit-ui';

interface ChecklistQuestionView {
  question: AuditTemplateQuestion;
  response: AuditResponse;
}

interface ChecklistSectionView {
  sectionName?: string;
  questions: ChecklistQuestionView[];
}

@Component({
  selector: 'app-audit-case-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading || saving) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Workspace" [title]="auditCase?.auditNumber || 'Audit Case'" [subtitle]="auditCase?.scopeDisplayName || auditCase?.sourceDisplayName || 'Manual audit execution'">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="true" severity="secondary"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/cases" label="Cases" icon="pi pi-arrow-left" [outlined]="true" severity="secondary"></a>
          <p-button label="Start" icon="pi pi-play" (onClick)="startCase()" [disabled]="!canStart"></p-button>
          <p-button label="Save Responses" icon="pi pi-save" severity="secondary" [outlined]="true" (onClick)="saveResponses()" [disabled]="!canSaveResponses"></p-button>
          <p-button label="Submit" icon="pi pi-send" severity="info" (onClick)="submitCase()" [disabled]="!canSubmit"></p-button>
          <p-button label="Review" icon="pi pi-check-square" severity="success" (onClick)="openReview()" [disabled]="!canReview"></p-button>
          <p-button label="Close" icon="pi pi-lock" severity="secondary" (onClick)="openClose()" [disabled]="!canClose"></p-button>
          <p-button label="Reopen" icon="pi pi-refresh" severity="warn" [outlined]="true" (onClick)="openReopen()" [disabled]="!canReopen"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Case Summary" subtitle="Lifecycle status, source reference, and scoring outcome." [resultText]="auditCase?.status || '-'">
        <div class="summary-grid">
          <div><span>Status</span><p-tag [value]="auditCase?.status || '-'" [severity]="statusSeverity(auditCase?.status)"></p-tag></div>
          <div><span>Result</span><strong>{{ auditCase?.passFailResult || '-' }}</strong></div>
          <div><span>Score</span><strong>{{ auditCase?.score ?? 0 }} / {{ auditCase?.maxScore ?? 0 }}</strong></div>
          <div><span>Module</span><strong>{{ auditCase?.moduleCode || '-' }}</strong></div>
          <div><span>Scope</span><strong>{{ auditCase?.scopeDisplayName || auditCase?.scopeNumber || auditCase?.scopeType || '-' }}</strong></div>
          <div><span>Source</span><strong>{{ auditCase?.sourceDisplayName || auditCase?.sourceResourceNumber || '-' }}</strong></div>
          <div><span>Started</span><strong>{{ formatDate(auditCase?.startedAt) }}</strong></div>
          <div><span>Submitted</span><strong>{{ formatDate(auditCase?.submittedAt) }}</strong></div>
          <div><span>Closed</span><strong>{{ formatDate(auditCase?.closedAt) }}</strong></div>
        </div>
      </app-page-data-panel>

      @if (auditCase?.sourceModule && auditCase?.sourceResourceType && auditCase?.sourceResourceId) {
        <app-page-data-panel title="Source Record" subtitle="Read-only LogiLink source snapshot and cross-module evidence timeline." [resultText]="sourcePreview?.status || auditCase?.sourceResourceNumber || '-'">
          <div actions>
            @if (sourcePreview?.sourceRoute) {
              <a pButton [routerLink]="sourcePreview?.sourceRoute" label="Open Source" icon="pi pi-external-link" [outlined]="true" severity="secondary"></a>
            }
            <p-button label="Import Snapshot" icon="pi pi-download" [outlined]="true" severity="secondary" (onClick)="importSourceEvidence()" [loading]="sourceImporting" [disabled]="!canUploadEvidence || !sourcePreview"></p-button>
          </div>

          @if (sourceLoading) {
            <p-progressBar mode="indeterminate"></p-progressBar>
          } @else if (sourcePreview) {
            <div class="source-summary">
              <div>
                <span>Source</span>
                <strong>{{ sourcePreview.sourceDisplayName || sourcePreview.sourceResourceNumber }}</strong>
                <small>{{ sourcePreview.sourceModule }}/{{ sourcePreview.sourceResourceType }} - {{ sourcePreview.sourceResourceNumber || sourcePreview.sourceResourceId }}</small>
              </div>
              <div>
                <span>Status</span>
                <p-tag [value]="sourcePreview.status || '-'" [severity]="statusSeverity(sourcePreview.status)"></p-tag>
              </div>
              <div>
                <span>Updated</span>
                <strong>{{ formatDate(sourcePreview.sourceUpdatedAt) }}</strong>
              </div>
              <div class="wide-field">
                <span>Summary</span>
                <strong>{{ sourcePreview.summary || '-' }}</strong>
              </div>
            </div>

            @if (sourceAttributeEntries.length) {
              <div class="source-attributes">
                @for (entry of sourceAttributeEntries; track entry.key) {
                  <div>
                    <span>{{ entry.key }}</span>
                    <strong>{{ entry.value }}</strong>
                  </div>
                }
              </div>
            }

            <p-table [value]="sourceTimeline" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '760px' }">
              <ng-template pTemplate="header"><tr><th>Timeline</th><th>Status</th><th>Time</th><th>Type</th></tr></ng-template>
              <ng-template pTemplate="body" let-event>
                <tr>
                  <td><div class="audit-stack"><strong>{{ event.eventType || '-' }}</strong><small>{{ event.title || '-' }}</small></div></td>
                  <td>{{ event.fromStatus || '-' }} -> {{ event.toStatus || '-' }}</td>
                  <td>{{ formatDate(event.eventTime) }}</td>
                  <td>{{ event.timelineType || '-' }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage"><tr><td colspan="4" class="text-center p-4">No source timeline events found.</td></tr></ng-template>
            </p-table>
          } @else {
            <div class="audit-empty">The linked source record could not be loaded for this tenant.</div>
          }
        </app-page-data-panel>
      }

      <app-page-data-panel title="Checklist" subtitle="Answer controls, attach evidence metadata, and generate findings from failures." [resultText]="answeredCount + ' answered'">
        @if (!sections.length) {
          <div class="audit-empty">No checklist questions are available.</div>
        }
        @for (section of sections; track section.sectionName || $index) {
          <section class="checklist-section">
            <h3>{{ section.sectionName || 'Section' }}</h3>
            @for (row of section.questions; track row.question.questionId) {
              <div class="question-row">
                <div class="question-copy">
                  <strong>{{ row.question.questionText }}</strong>
                  <small>{{ row.question.controlCode || 'Control' }} · {{ row.question.answerType || 'PASS_FAIL' }} · Weight {{ row.question.weight || 1 }}</small>
                  <div class="question-tags">
                    @if (row.question.mandatory) { <p-tag value="Mandatory" severity="info"></p-tag> }
                    @if (row.question.critical) { <p-tag value="Critical" severity="danger"></p-tag> }
                    @if (row.question.evidenceRequired) { <p-tag value="Evidence" severity="warn"></p-tag> }
                    @if (row.question.findingOnFailure) { <p-tag value="Auto finding" severity="secondary"></p-tag> }
                  </div>
                </div>
                <div class="response-grid">
                  @if (row.question.answerType === 'SCORE') {
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-star"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-inputnumber [inputId]="'case-score-' + row.question.questionId" [(ngModel)]="row.response.scoreAwarded" [min]="0" [max]="row.question.weight || 1" [maxFractionDigits]="2" [disabled]="!canSaveResponses"></p-inputnumber>
                        <label [attr.for]="'case-score-' + row.question.questionId">Score</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-eye"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <input pInputText [id]="'case-observed-value-' + row.question.questionId" [(ngModel)]="row.response.answerValue" [disabled]="!canSaveResponses">
                        <label [attr.for]="'case-observed-value-' + row.question.questionId">Observed value</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  } @else {
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-check-square"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-select [inputId]="'case-result-' + row.question.questionId" [options]="responseResultOptions" [(ngModel)]="row.response.result" optionLabel="label" optionValue="value" [disabled]="!canSaveResponses" placeholder="Select result"></p-select>
                        <label [attr.for]="'case-result-' + row.question.questionId">Result</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-pencil"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <input pInputText [id]="'case-answer-value-' + row.question.questionId" [(ngModel)]="row.response.answerValue" [disabled]="!canSaveResponses" placeholder="Optional">
                        <label [attr.for]="'case-answer-value-' + row.question.questionId">Answer value</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  }
                  <p-inputgroup class="comment-field">
                    <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <textarea pTextarea rows="2" [id]="'case-response-comment-' + row.question.questionId" [(ngModel)]="row.response.comment" [disabled]="!canSaveResponses"></textarea>
                      <label [attr.for]="'case-response-comment-' + row.question.questionId">Comment</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <div class="row-actions">
                    <p-button label="Evidence" icon="pi pi-paperclip" [outlined]="true" severity="secondary" (onClick)="openEvidence(row)" [disabled]="!canUploadEvidence"></p-button>
                    <p-button label="Finding" icon="pi pi-exclamation-triangle" [outlined]="true" severity="secondary" (onClick)="openFinding(row)" [disabled]="!canManageFindings"></p-button>
                  </div>
                </div>
              </div>
            }
          </section>
        }
      </app-page-data-panel>

      <div class="audit-grid-two">
        <app-page-data-panel title="Evidence" subtitle="Metadata links to files, URLs, hashes, and source records." [resultText]="(auditCase?.evidence?.length || 0) + ' items'">
          <div actions>
            <p-button label="Add Evidence" icon="pi pi-plus" [outlined]="true" severity="secondary" (onClick)="openEvidence()" [disabled]="!canUploadEvidence"></p-button>
          </div>
          <p-table [value]="auditCase?.evidence || []" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Evidence</th><th>Type</th><th>Hash</th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><div class="audit-stack"><strong>{{ item.fileName || item.fileUrl || item.sourceDisplayName || '-' }}</strong><small>{{ item.fileUrl || item.sourceResourceNumber || '-' }}</small></div></td>
                <td>{{ item.evidenceType || '-' }}</td>
                <td class="hash-cell">{{ item.sha256Hash || '-' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage"><tr><td colspan="3" class="text-center p-4">No evidence metadata yet.</td></tr></ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Findings" subtitle="Manual and auto-created nonconformities." [resultText]="(auditCase?.findings?.length || 0) + ' findings'">
          <div actions>
            <p-button label="Add Finding" icon="pi pi-plus" [outlined]="true" severity="secondary" (onClick)="openFinding()" [disabled]="!canManageFindings"></p-button>
          </div>
          <p-table [value]="auditCase?.findings || []" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Finding</th><th>Severity</th><th>Status</th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><div class="audit-stack"><strong>{{ item.title }}</strong><small>{{ item.findingNumber || '-' }}</small></div></td>
                <td><p-tag [value]="item.severity || '-'" [severity]="statusSeverity(item.severity)"></p-tag></td>
                <td><p-tag [value]="item.status || '-'" [severity]="statusSeverity(item.status)"></p-tag></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage"><tr><td colspan="3" class="text-center p-4">No findings yet.</td></tr></ng-template>
          </p-table>
        </app-page-data-panel>
      </div>

      <app-page-data-panel title="Event History" subtitle="Lifecycle and audit actions written by the backend." [resultText]="(auditCase?.events?.length || 0) + ' events'">
        <p-table [value]="auditCase?.events || []" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '760px' }">
          <ng-template pTemplate="header"><tr><th>Event</th><th>Status</th><th>Performed</th><th>Comment</th></tr></ng-template>
          <ng-template pTemplate="body" let-event>
            <tr>
              <td>{{ event.eventType }}</td>
              <td>{{ event.fromStatus || '-' }} → {{ event.toStatus || '-' }}</td>
              <td>{{ formatDate(event.performedAt) }}</td>
              <td>{{ event.comment || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="4" class="text-center p-4">No audit events yet.</td></tr></ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog header="Evidence Metadata" [(visible)]="evidenceDialogVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="dialog-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="case-evidence-type" [options]="evidenceTypeOptions" [(ngModel)]="evidenceForm.evidenceType" optionLabel="label" optionValue="value"></p-select>
            <label for="case-evidence-type">Evidence type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-eye"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-evidence-visibility" [(ngModel)]="evidenceForm.visibility">
            <label for="case-evidence-visibility">Visibility</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-evidence-file-name" [(ngModel)]="evidenceForm.fileName">
            <label for="case-evidence-file-name">File name</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-evidence-file-url" [(ngModel)]="evidenceForm.fileUrl">
            <label for="case-evidence-file-url">File URL</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-evidence-content-type" [(ngModel)]="evidenceForm.contentType">
            <label for="case-evidence-content-type">Content type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-evidence-sha" [(ngModel)]="evidenceForm.sha256Hash">
            <label for="case-evidence-sha">SHA-256 hash</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) { <div class="audit-error">{{ dialogError }}</div> }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="evidenceDialogVisible = false"></p-button>
        <p-button label="Add Evidence" icon="pi pi-check" (onClick)="saveEvidence()" [loading]="saving" [disabled]="!canUploadEvidence"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Audit Finding" [(visible)]="findingDialogVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="dialog-grid">
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-heading"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="case-finding-title" [(ngModel)]="findingForm.title">
            <label for="case-finding-title">Title</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="case-finding-severity" [options]="severityOptions" [(ngModel)]="findingForm.severity" optionLabel="label" optionValue="value"></p-select>
            <label for="case-finding-severity">Severity</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="case-finding-description" rows="4" [(ngModel)]="findingForm.description"></textarea>
            <label for="case-finding-description">Description</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) { <div class="audit-error">{{ dialogError }}</div> }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="findingDialogVisible = false"></p-button>
        <p-button label="Add Finding" icon="pi pi-check" (onClick)="saveFinding()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog [header]="reviewDialogTitle" [(visible)]="reviewDialogVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(620px, 96vw)' }">
      <div class="dialog-grid">
        @if (reviewMode === 'review') {
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-review-result" [options]="reviewResultOptions" [(ngModel)]="reviewForm.result" optionLabel="label" optionValue="value"></p-select>
              <label for="case-review-result">Review result</label>
            </p-iftalabel>
          </p-inputgroup>
        }
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="case-review-comment" rows="4" [(ngModel)]="reviewForm.comment"></textarea>
            <label for="case-review-comment">{{ reviewMode === 'reopen' ? 'Reopen reason' : 'Comment' }}</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="reviewDialogVisible = false"></p-button>
        <p-button [label]="reviewMode === 'close' ? 'Close Case' : reviewMode === 'reopen' ? 'Reopen Case' : 'Save Review'" [icon]="reviewMode === 'reopen' ? 'pi pi-refresh' : 'pi pi-check'" (onClick)="saveReviewOrClose()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .summary-grid,
    .source-summary,
    .source-attributes,
    .dialog-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(170px, 1fr));
      gap: .85rem;
    }

    .summary-grid > div,
    .source-summary > div,
    .source-attributes > div {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      display: grid;
      gap: .25rem;
      min-height: 4.25rem;
      padding: .85rem;
    }

    .summary-grid span,
    .source-summary span,
    .source-attributes span,
    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    .summary-grid strong,
    .source-summary strong,
    .source-attributes strong {
      color: #172033;
      overflow-wrap: anywhere;
    }

    .source-summary small {
      color: #64748b;
      font-size: .78rem;
    }

    .source-attributes {
      margin: .85rem 0;
    }

    .checklist-section {
      border: 1px solid #dfe7f3;
      border-radius: 8px;
      margin-bottom: .85rem;
      padding: .85rem;
    }

    .checklist-section h3 {
      color: #172033;
      font-size: 1rem;
      margin: 0 0 .75rem;
    }

    .question-row {
      border-top: 1px solid #e2e8f0;
      display: grid;
      grid-template-columns: minmax(260px, .8fr) minmax(0, 1.2fr);
      gap: .85rem;
      padding: .85rem 0;
    }

    .question-row:first-of-type {
      border-top: 0;
      padding-top: 0;
    }

    .question-copy {
      display: grid;
      gap: .35rem;
    }

    .question-copy small,
    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .question-tags,
    .row-actions {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: .4rem;
    }

    .response-grid {
      display: grid;
      grid-template-columns: minmax(130px, .35fr) minmax(170px, .55fr) minmax(220px, 1fr) auto;
      gap: .65rem;
      align-items: end;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputnumber {
      width: 100%;
    }

    .audit-grid-two {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }

    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .hash-cell {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
      max-width: 220px;
      overflow-wrap: anywhere;
    }

    .wide-field {
      grid-column: 1 / -1;
    }

    .audit-empty,
    .audit-error {
      border-radius: 8px;
      font-weight: 700;
      padding: .85rem;
    }

    .audit-empty {
      border: 1px dashed #cbd5e1;
      color: #64748b;
      text-align: center;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #991b1b;
      margin-top: .85rem;
    }

    @media (max-width: 1100px) {
      .summary-grid,
      .source-summary,
      .source-attributes,
      .dialog-grid,
      .question-row,
      .response-grid,
      .audit-grid-two {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditCaseDetailComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  sourceLoading = false;
  sourceImporting = false;
  auditCase?: AuditCaseDetail;
  sourcePreview?: AuditSourceResource;
  sourceTimeline: AuditSourceTimelineEvent[] = [];
  sections: ChecklistSectionView[] = [];
  evidenceDialogVisible = false;
  findingDialogVisible = false;
  reviewDialogVisible = false;
  reviewMode: 'review' | 'close' | 'reopen' = 'review';
  dialogError = '';
  evidenceForm: AuditEvidence = {};
  findingForm: AuditFinding = {};
  reviewForm = { result: 'PASSED', comment: '' };

  readonly responseResultOptions = responseResultOptions;
  readonly evidenceTypeOptions = evidenceTypeOptions;
  readonly severityOptions = severityOptions;
  readonly reviewResultOptions = reviewResultOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canStartPermission = this.permissionService.hasPermission(app_permissions.audit.cases.start);
  readonly canEditPermission = this.permissionService.hasPermission(app_permissions.audit.cases.edit);
  readonly canSubmitPermission = this.permissionService.hasPermission(app_permissions.audit.cases.submit);
  readonly canReviewPermission = this.permissionService.hasPermission(app_permissions.audit.cases.review);
  readonly canClosePermission = this.permissionService.hasPermission(app_permissions.audit.cases.close);
  readonly canReopenPermission = this.permissionService.hasPermission(app_permissions.audit.cases.reopen);
  readonly canUploadEvidence = this.permissionService.hasPermission(app_permissions.audit.evidence.upload);
  readonly canManageFindings = this.permissionService.hasPermission(app_permissions.audit.findings.manage);
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get auditCaseId(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  get canStart(): boolean {
    return this.canStartPermission && this.auditCase?.status === 'DRAFT';
  }

  get canSaveResponses(): boolean {
    return this.canEditPermission && this.auditCase?.status === 'IN_PROGRESS';
  }

  get canSubmit(): boolean {
    return this.canSubmitPermission && this.auditCase?.status === 'IN_PROGRESS';
  }

  get canReview(): boolean {
    return this.canReviewPermission && this.auditCase?.status === 'SUBMITTED';
  }

  get canClose(): boolean {
    return this.canClosePermission && ['PASSED', 'FAILED', 'NEEDS_CORRECTION'].includes(this.auditCase?.status || '');
  }

  get canReopen(): boolean {
    return this.canReopenPermission && ['PASSED', 'FAILED', 'CLOSED'].includes(this.auditCase?.status || '');
  }

  get reviewDialogTitle(): string {
    return this.reviewMode === 'close'
      ? 'Close Audit Case'
      : this.reviewMode === 'reopen'
        ? 'Reopen Audit Case'
        : 'Review Audit Case';
  }

  get answeredCount(): number {
    return this.sections.flatMap(section => section.questions)
      .filter(row => row.response.result || row.response.answerValue || row.response.scoreAwarded !== undefined).length;
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Questions', value: this.sections.flatMap(section => section.questions).length, tone: 'info' },
      { label: 'Answered', value: this.answeredCount, tone: 'success' },
      { label: 'Evidence', value: this.auditCase?.evidence?.length || 0 },
      { label: 'Findings', value: this.auditCase?.findings?.length || 0, tone: 'warn' },
      { label: 'Score', value: `${this.auditCase?.score ?? 0}/${this.auditCase?.maxScore ?? 0}` }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.getCase(this.auditCaseId).subscribe({
      next: response => {
        this.auditCase = response.data;
        this.buildChecklist();
        this.loadSourceContext();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Case', detail: 'Unable to load audit case.' }),
      complete: () => this.loading = false
    });
  }

  startCase(): void {
    this.runAction(this.auditService.startCase(this.auditCaseId), 'Case started');
  }

  saveResponses(): void {
    const responses = this.sections.flatMap(section => section.questions).map(row => row.response);
    this.saving = true;
    this.auditService.saveResponses(this.auditCaseId, { responses }).subscribe({
      next: response => {
        this.auditCase = response.data;
        this.buildChecklist();
        this.messageService.add({ severity: 'success', summary: 'Responses saved', detail: 'Checklist responses saved.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Responses', detail: error?.error?.message || 'Unable to save responses.' }),
      complete: () => this.saving = false
    });
  }

  submitCase(): void {
    this.runAction(this.auditService.submitCase(this.auditCaseId), 'Case submitted');
  }

  openReview(): void {
    this.reviewMode = 'review';
    this.reviewForm = { result: this.auditCase?.passFailResult || 'PASSED', comment: '' };
    this.dialogError = '';
    this.reviewDialogVisible = true;
  }

  openClose(): void {
    this.reviewMode = 'close';
    this.reviewForm = { result: this.auditCase?.status || 'PASSED', comment: '' };
    this.dialogError = '';
    this.reviewDialogVisible = true;
  }

  openReopen(): void {
    this.reviewMode = 'reopen';
    this.reviewForm = { result: 'NEEDS_CORRECTION', comment: '' };
    this.dialogError = '';
    this.reviewDialogVisible = true;
  }

  saveReviewOrClose(): void {
    this.dialogError = '';
    if (this.reviewMode === 'reopen' && !this.reviewForm.comment.trim()) {
      this.dialogError = 'A reopen reason is required.';
      return;
    }
    const request = { ...this.reviewForm };
    const action = this.reviewMode === 'close'
      ? this.auditService.closeCase(this.auditCaseId, request)
      : this.reviewMode === 'reopen'
        ? this.auditService.reopenCase(this.auditCaseId, { comment: request.comment, reason: request.comment })
        : this.auditService.reviewCase(this.auditCaseId, request);
    this.saving = true;
    action.subscribe({
      next: () => {
        this.reviewDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Case updated',
          detail: this.reviewMode === 'close' ? 'Case closed.' : this.reviewMode === 'reopen' ? 'Case reopened.' : 'Review saved.'
        });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Case', detail: error?.error?.message || 'Unable to update case.' }),
      complete: () => this.saving = false
    });
  }

  openEvidence(row?: ChecklistQuestionView): void {
    this.dialogError = '';
    this.evidenceForm = {
      auditCaseId: this.auditCaseId,
      responseId: row?.response.responseId,
      questionId: row?.question.questionId,
      evidenceType: 'DOCUMENT',
      visibility: 'INTERNAL'
    } as AuditEvidence;
    this.evidenceDialogVisible = true;
  }

  saveEvidence(): void {
    this.dialogError = '';
    if (!this.evidenceForm.evidenceType) {
      this.dialogError = 'Evidence type is required.';
      return;
    }
    this.saving = true;
    this.auditService.createEvidence(this.evidenceForm).subscribe({
      next: () => {
        this.evidenceDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Evidence added', detail: 'Evidence metadata added.' });
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to add evidence metadata.',
      complete: () => this.saving = false
    });
  }

  importSourceEvidence(): void {
    if (!this.auditCase?.sourceModule || !this.auditCase.sourceResourceType || !this.auditCase.sourceResourceId) {
      return;
    }
    this.sourceImporting = true;
    this.auditService.importSourceEvidence(this.auditCaseId, {
      sourceModule: this.auditCase.sourceModule,
      sourceResourceType: this.auditCase.sourceResourceType,
      sourceResourceId: this.auditCase.sourceResourceId,
      sourceResourceNumber: this.auditCase.sourceResourceNumber,
      sourceDisplayName: this.auditCase.sourceDisplayName,
      evidenceType: 'SOURCE_SNAPSHOT',
      visibility: 'INTERNAL',
      comment: 'Source snapshot imported from audit case workspace.'
    }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Source evidence', detail: 'Source snapshot imported as evidence.' });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Source evidence', detail: error?.error?.message || 'Unable to import source evidence.' }),
      complete: () => this.sourceImporting = false
    });
  }

  openFinding(row?: ChecklistQuestionView): void {
    this.dialogError = '';
    this.findingForm = {
      auditCaseId: this.auditCaseId,
      questionId: row?.question.questionId,
      title: row ? `Finding: ${row.question.controlCode || row.question.questionText}` : '',
      severity: row?.question.critical ? 'CRITICAL' : 'MEDIUM'
    };
    this.findingDialogVisible = true;
  }

  saveFinding(): void {
    this.dialogError = '';
    if (!this.findingForm.title?.trim()) {
      this.dialogError = 'Finding title is required.';
      return;
    }
    this.saving = true;
    this.auditService.createFinding(this.findingForm).subscribe({
      next: () => {
        this.findingDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Finding added', detail: 'Audit finding added.' });
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to add finding.',
      complete: () => this.saving = false
    });
  }

  private buildChecklist(): void {
    const responseByQuestion = new Map<number, AuditResponse>();
    for (const response of this.auditCase?.responses || []) {
      if (response.questionId) {
        responseByQuestion.set(response.questionId, { ...response });
      }
    }
    this.sections = (this.auditCase?.templateVersion?.sections || []).map(section => ({
      sectionName: section.sectionName,
      questions: (section.questions || []).map(question => ({
        question,
        response: responseByQuestion.get(question.questionId!) || {
          auditCaseId: this.auditCaseId,
          questionId: question.questionId,
          result: question.answerType === 'SCORE' ? undefined : 'PASS'
        }
      }))
    }));
  }

  private loadSourceContext(): void {
    this.sourcePreview = undefined;
    this.sourceTimeline = [];
    if (!this.auditCase?.sourceModule || !this.auditCase.sourceResourceType || !this.auditCase.sourceResourceId) {
      return;
    }
    this.sourceLoading = true;
    forkJoin({
      preview: this.auditService.sourcePreview(this.auditCase.sourceModule, this.auditCase.sourceResourceType, this.auditCase.sourceResourceId),
      timeline: this.auditService.sourceTimeline(this.auditCase.sourceModule, this.auditCase.sourceResourceType, this.auditCase.sourceResourceId)
    }).subscribe({
      next: response => {
        this.sourcePreview = response.preview.data;
        this.sourceTimeline = response.timeline.data ?? [];
      },
      error: () => {
        this.sourcePreview = undefined;
        this.sourceTimeline = [];
      },
      complete: () => this.sourceLoading = false
    });
  }

  get sourceAttributeEntries(): Array<{ key: string; value: string }> {
    return Object.entries(this.sourcePreview?.attributes || {})
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .slice(0, 12)
      .map(([key, value]) => ({ key, value: String(value) }));
  }

  private runAction(action: ReturnType<AuditService['startCase']>, successSummary: string): void {
    this.saving = true;
    action.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: successSummary, detail: successSummary });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Case', detail: error?.error?.message || 'Unable to update audit case.' }),
      complete: () => this.saving = false
    });
  }
}
